import engine from './engine';
import Scene3D from 'apollo-e/Scene3D';
import PerspectiveCamera from 'apollo-e/rendering/cameras/PerspectiveCamera';
import GameObject from 'apollo-e/GameObject';
import { TextureSet } from 'apollo-e/rendering/Texture';
import opacities from 'apollo-e/rendering/OPACITIES';
import CopyDark from './components/CopyDark';
import CopyOutlined from './components/CopyOutlined';
import QT3D from './components/QT3D';
import CCContainer3D from './components/CCContainer3D';
import { getSDFFont } from 'apollo-e/rendering/SDFFont';
import BitmapText3D from './components/BitmapText3D';

import Scroller from './arcade/Scroller';
import Sheep from './arcade/Sheep';
import World from './arcade/World';
import { fetchSolPriceAnchorLast5Minutes } from './arcade/arcadeRNGAnchor';
import type { SolPriceAnchor } from './arcade/arcadeRNGAnchor';
import { ArcadeSim } from './sim/ArcadeSim';
import { ARCADE_SIM_DT_MS, createGameSimulation, createPackedInputsCursor } from './sim';
import { REPLAY_SESSION } from './arcade/replaySession';
import { getOGPConfig, getOGPSession, getOpenGameSDK, isEmbeddedInPlayFunPlatform, submitScoreValidation, waitForOGPPlayerId } from './ogpClient';
import type { ScoreValidationRequest } from './shared/scoreValidation';

type ArcadeSimSnapshot = import('./sim/ArcadeSim').ArcadeSimSnapshot;
type ArcadeSimInput = import('./sim/ArcadeSim').ArcadeSimInput;
type ArcadeSimEvent = import('./sim/ArcadeSim').ArcadeSimEvent;
type GameSimInput = import('./sim').GameSimulationInput;
type GameSimSessionData = import('./sim').GameSimulationSessionData;

function roundToSigDigits(x: number, sig: number) {
  if (!Number.isFinite(x)) {
    return x;
  }
  if (x === 0) {
    return 0;
  }
  // 3 significant digits (not decimals).
  return Number.parseFloat(x.toPrecision(sig));
}

function lerp (a: number, b: number, t: number) {
  return a + (b - a) * t;
}

function lerpAngle (a: number, b: number, t: number) {
  let d = (b - a) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return a + d * t;
}

// Camera layout knobs:
// - In 9:16 (portrait), we target a fixed "game height" H (world units).
// - When the view gets wider, reduce height linearly: every `CAMERA_EXTRA_WIDTH_UNITS_PER_HEIGHT_UNIT`
//   extra world-units of width reduces the camera height by 1 world-unit.
const CAMERA_PORTRAIT_ASPECT_9_16 = 9 / 16;
// Default chosen to preserve the current feel in 9:16 with the existing camera setup.
// Tweak this to change the "base" game height in portrait.
const CAMERA_GAME_HEIGHT_PORTRAIT_H = 1364.4476141053262;
const CAMERA_EXTRA_WIDTH_UNITS_PER_HEIGHT_UNIT = 4;
const CAMERA_MIN_GAME_HEIGHT = 720;

function computeTargetGameHeightForAspect(aspect: number) {
  const H = CAMERA_GAME_HEIGHT_PORTRAIT_H;
  if (!Number.isFinite(aspect) || aspect <= 0 || aspect <= CAMERA_PORTRAIT_ASPECT_9_16) {
    return { gameHeight: H, heightReduction: 0 };
  }

  // Enforce: (width - baseWidth) = (H - h) * k
  // where width = h * aspect, baseWidth = H * baseAspect, k = extra width per 1 unit height reduction.
  // Solve for h:
  //   h = H * (1 + baseAspect / k) / (1 + aspect / k)
  const k = CAMERA_EXTRA_WIDTH_UNITS_PER_HEIGHT_UNIT;
  const hRaw = H * (1 + CAMERA_PORTRAIT_ASPECT_9_16 / k) / (1 + aspect / k);
  const h = Math.max(CAMERA_MIN_GAME_HEIGHT, hRaw);
  return { gameHeight: h, heightReduction: H - h };
}

const computeAltitude = (y: number) => {
  return (y + 400) / 120;
};

const getPerfNowMs = () => {
  const p: any = (typeof performance !== 'undefined' ? performance : null);
  return p && typeof p.now === 'function' ? p.now() : Date.now();
};

const normalizeEventTimeStamp = (timeStamp?: number) => {
  if (!Number.isFinite(timeStamp as number)) {
    return null;
  }
  const ts = timeStamp as number;
  if (ts > 1e12) {
    const p: any = (typeof performance !== 'undefined' ? performance : null);
    if (p && Number.isFinite(p.timeOrigin)) {
      return ts - p.timeOrigin;
    }
  }
  return ts;
};

export default function initArcade (assets: any) {
  // Toggle this to replay the hard-coded `REPLAY_SESSION` instead of running live input.
  const REPLAY_MODE = false;
  // Toggle this to run the simulation headlessly (no visuals) and log performance timing.
  // Uses `REPLAY_SESSION` as the workload.
  const HEADLESS_BENCHMARK_MODE = false;
  // Debug: simulate very low render FPS (0 disables).
  const DEBUG_RENDER_FPS = 0;

  if (HEADLESS_BENCHMARK_MODE) {
    const seedParts = REPLAY_SESSION.seedParts.slice();
    const dtMs = REPLAY_SESSION.dtMs;
    const inputsPacked = REPLAY_SESSION.inputsPacked;

    const now = () => {
      const p: any = (typeof performance !== 'undefined' ? performance : null);
      return p && typeof p.now === 'function' ? p.now() : Date.now();
    };

    const iterations = 100;
    const warmup = 0;

    // Warm up JIT
    for (let i = 0; i < warmup; i++) {
      ArcadeSim.run(seedParts, inputsPacked as any, dtMs, { stopOnGameOver: false, captureSnapshots: false });
    }

    let minMs = Infinity;
    let maxMs = -Infinity;
    let sumMs = 0;
    let last: any = null;

    const startWall = now();
    for (let i = 0; i < iterations; i++) {
      const res = ArcadeSim.run(seedParts, inputsPacked as any, dtMs, { stopOnGameOver: false, captureSnapshots: false });
      last = res;
      minMs = Math.min(minMs, res.elapsedMs);
      maxMs = Math.max(maxMs, res.elapsedMs);
      sumMs += res.elapsedMs;
    }
    const endWall = now();

    const avgMs = sumMs / iterations;
    const stepsPerSec = last && last.simulatedSteps ? (last.simulatedSteps / avgMs) * 1000 : null;

    console.log('[headlessBenchmark]', {
      seedParts,
      dtMs,
      inputSegments: last?.inputSegments,
      simulatedSteps: last?.simulatedSteps,
      gameOverStepIndex: last?.gameOverStepIndex,
      events: last?.events?.length,
      elapsedMs: { avg: avgMs, min: minMs, max: maxMs },
      stepsPerSec,
      wallMs: endWall - startWall,
      iterations,
      warmup,
    });

    return;
  }

  // Per-session seed: captured on FIRST player interaction (start of the session).
  // The simulation owns the seeded RNG; rendering code must not consume deterministic RNG.
  let playerKey = 'player-default';
  let sessionStartTimeMs: number | null = null;
  let sim: ArcadeSim | null = null;
  let gameSim: ReturnType<typeof createGameSimulation> | null = null;
  let gameSimSession: GameSimSessionData | null = null;
  let didEndGameSim = false;
  let lastStepResult: { events: ArcadeSimEvent[]; snapshot: ArcadeSimSnapshot } | null = null;
  let prevSnapshot: ArcadeSimSnapshot | null = null;
  let currSnapshot: ArcadeSimSnapshot | null = null;
  let simInputState: ArcadeSimInput = { thrustActive: false, thrustVector: { x: 0, y: 1 } };
  let pendingInputEvents: Array<{ step: number; thrustActive: boolean; thrustVector: { x: number; y: number } }> = [];
  let nextInputEventIndex = 0;
  let nonce: string[] | null = null;
  let nonceAnchorMeta: SolPriceAnchor | null = null;
  let simStepIndex: number = 0;
  let didLogGameOverSession = false;
  let hasSessionStarted = false;
  let isInitializingSession = false;
  let needsInputToResume = false;
  let pauseAfterReset = false;
  let resumeRequestedDuringGameOver = false;
  let resumeRequestedTouches: any = null;
  let pendingStartEvent: any = null;
  let pendingPreStartTouches: any = null;
  let preStartTouches: any = {};
  const ANCHOR_TTL_MS = 120_000;
  let nextAnchor: SolPriceAnchor | null = null;
  let nextAnchorPromise: Promise<SolPriceAnchor> | null = null;
  let nextAnchorRequestedAtMs: number | null = null;
  let nextAnchorRequestId = 0;
  let didSubmitScore = false;
  let scoreValidationSettledAtMs: number | null = null;
  const SCORE_VALIDATION_DELAY_MS = 1_500;
  const SCORE_SUBMISSION_FREEFALL_MS = 3_000;
  const SCORE_SUBMIT_MAX_RETRIES = 3;
  const SCORE_SUBMIT_RETRY_DELAY_MS = 2_000;
  const SCORE_SESSION_MAX_AGE_MS = 10 * 60 * 1000;
  const NON_RETRYABLE_ERRORS = new Set([
    'invalid_body',
    'session_data_required',
    'seed_parts_required',
    'session_start_required',
    'invalid_dt',
    'packed_inputs_required',
    'seed_mismatch',
    'local_seed_disallowed',
    'invalid_endTick',
    'endTick_too_large',
    'invalid_inputs',
  ]);
  let pendingScoreValidationTimeout: number | null = null;
  let pendingScoreValidationTrigger: string | null = null;
  let didScheduleScoreValidation = false;
  let gameOverTick: number | null = null;
  let gameOverStepIndex: number | null = null;
  let mouseDown = false;
  let resolvedOGPSessionForRun: { playerId?: string; gameId?: string; sessionToken?: string } | null = null;
  let rngLoadingOverlayEl: HTMLDivElement | null = null;
  let rngLoadingTextEl: HTMLDivElement | null = null;
  let rngLoadingIntervalId: number | null = null;

  const RNG_LOADING_STEPS = ['l', 'lo', 'loa', 'load', 'loadi', 'loadin', 'loading'];

  const ensureRngLoadingOverlay = () => {
    if (rngLoadingOverlayEl) {
      return;
    }
    const overlay = document.createElement('div');
    overlay.style.position = 'fixed';
    overlay.style.inset = '0';
    overlay.style.background = 'rgba(0, 0, 0, 0.3)';
    overlay.style.display = 'none';
    overlay.style.alignItems = 'center';
    overlay.style.justifyContent = 'center';
    overlay.style.zIndex = '10000';
    overlay.style.pointerEvents = 'none';

    const text = document.createElement('div');
    text.style.color = '#ffffff';
    text.style.fontFamily = 'monospace';
    text.style.fontSize = '28px';
    text.style.letterSpacing = '1px';
    text.textContent = RNG_LOADING_STEPS[0];

    overlay.appendChild(text);
    document.body.appendChild(overlay);

    rngLoadingOverlayEl = overlay;
    rngLoadingTextEl = text;
  };

  const prewarmRngLoadingOverlay = () => {
    if (typeof document === 'undefined') {
      return;
    }
    if (document.body) {
      ensureRngLoadingOverlay();
      return;
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('DOMContentLoaded', () => ensureRngLoadingOverlay(), { once: true });
    }
  };

  const showRngLoadingOverlay = () => {
    ensureRngLoadingOverlay();
    if (!rngLoadingOverlayEl || !rngLoadingTextEl) {
      return;
    }
    rngLoadingOverlayEl.style.display = 'flex';

    if (rngLoadingIntervalId !== null) {
      window.clearInterval(rngLoadingIntervalId);
      rngLoadingIntervalId = null;
    }

    let stepIndex = 0;
    rngLoadingTextEl.textContent = RNG_LOADING_STEPS[stepIndex];
    rngLoadingIntervalId = window.setInterval(() => {
      stepIndex = (stepIndex + 1) % RNG_LOADING_STEPS.length;
      if (rngLoadingTextEl) {
        rngLoadingTextEl.textContent = RNG_LOADING_STEPS[stepIndex];
      }
    }, 180);
  };

  const hideRngLoadingOverlay = () => {
    if (rngLoadingIntervalId !== null) {
      window.clearInterval(rngLoadingIntervalId);
      rngLoadingIntervalId = null;
    }
    if (rngLoadingOverlayEl) {
      rngLoadingOverlayEl.style.display = 'none';
    }
  };

  const waitForNextPaint = async () => {
    await new Promise<void>((resolve) => {
      if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(() => resolve());
        return;
      }
      setTimeout(() => resolve(), 0);
    });
  };

  prewarmRngLoadingOverlay();

  const isAnchorFresh = (anchor: SolPriceAnchor | null, nowMs: number) => {
    return anchor !== null && nowMs - anchor.sessionStartTimeMs <= ANCHOR_TTL_MS;
  };

  const isAnchorRequestFresh = (requestTimeMs: number | null, nowMs: number) => {
    return requestTimeMs !== null && nowMs - requestTimeMs <= ANCHOR_TTL_MS;
  };

  const startAnchorPrefetch = (_reason: string, startTimeMs: number) => {
    const requestId = ++nextAnchorRequestId;
    nextAnchorRequestedAtMs = startTimeMs;
    const promise = fetchSolPriceAnchorLast5Minutes(startTimeMs)
      .then((anchor) => {
        if (requestId === nextAnchorRequestId) {
          nextAnchor = anchor;
        }
        return anchor;
      })
      .catch((err) => {
        if (requestId === nextAnchorRequestId) {
          nextAnchor = null;
        }
        throw err;
      })
      .finally(() => {
        if (requestId === nextAnchorRequestId) {
          nextAnchorPromise = null;
        }
      });
    nextAnchorPromise = promise;
    return promise;
  };

  const prefetchNextAnchor = (reason: string) => {
    if (REPLAY_MODE) {
      return null;
    }
    const nowMs = Date.now();
    if (isAnchorFresh(nextAnchor, nowMs)) {
      return Promise.resolve(nextAnchor as SolPriceAnchor);
    }
    if (nextAnchorPromise && isAnchorRequestFresh(nextAnchorRequestedAtMs, nowMs)) {
      return nextAnchorPromise;
    }
    return startAnchorPrefetch(reason, nowMs);
  };

  const queueNextAnchorPrefetch = (reason: string) => {
    const promise = prefetchNextAnchor(reason);
    if (promise) {
      promise.catch((err) => {
        console.warn('[arcadeSim] anchor prefetch failed', err);
      });
    }
  };

  const getLocalPlayerId = () => {
    const params = new URLSearchParams(window.location.search);
    const paramPlayerId = params.get('playerId');
    if (paramPlayerId) {
      return paramPlayerId;
    }
    const storageKey = 'apollo-local-player-id';
    let localId = window.localStorage?.getItem(storageKey) || '';
    if (!localId) {
      localId = `local-${Math.random().toString(36).slice(2, 10)}`;
      window.localStorage?.setItem(storageKey, localId);
    }
    return localId;
  };

  type ScoreValidationSession = {
    sessionStartTimeMs: number;
    sessionData: GameSimSessionData;
  };

  const finalizeGameSimSessionIfNeeded = (): GameSimSessionData | null => {
    if (!gameSim || !nonce) {
      return null;
    }
    if (gameSimSession) {
      return gameSimSession;
    }
    // Avoid ending mid-run; in this game, "end" happens once game-over has been observed.
    if (!didLogGameOverSession) {
      return null;
    }
    try {
      gameSimSession = gameSim.end();
      didEndGameSim = true;
      return gameSimSession;
    } catch (err) {
      console.warn('[score-validation] failed to finalize GameSimulation session', err);
      return null;
    }
  };

  const buildScoreValidationRunData = (): ScoreValidationSession | null => {
    if (!nonce || sessionStartTimeMs === null) {
      console.warn('[score-validation] missing session seed, skipping submission');
      return null;
    }

    const sessionData = finalizeGameSimSessionIfNeeded();
    if (!sessionData) {
      console.warn('[score-validation] missing session data, skipping submission');
      return null;
    }

    return {
      sessionStartTimeMs,
      sessionData,
    };
  };

  const submitScoreValidationForRun = async (trigger: string, session: ScoreValidationSession) => {
    if (REPLAY_MODE || didSubmitScore) {
      return;
    }
    didSubmitScore = true;
    scoreValidationSettledAtMs = null;

    try {
      // Skip if session is too old (> 10 minutes)
      if (Date.now() - session.sessionStartTimeMs > SCORE_SESSION_MAX_AGE_MS) {
        console.warn('[score-validation] session too old, skipping', { trigger });
        return;
      }

      const config = getOGPConfig();
      let resolvedGameId = resolvedOGPSessionForRun?.gameId || config.gameId;
      let resolvedSessionToken = resolvedOGPSessionForRun?.sessionToken;
      let resolvedPlayerId = resolvedOGPSessionForRun?.playerId;

      if (!resolvedPlayerId || !resolvedSessionToken) {
        const ogpSession = await getOGPSession({
          requireLogin: true,
          loginTimeoutMs: 10_000,
          loginRetries: 2,
        }).catch(() => null);
        if (ogpSession) {
          resolvedGameId = ogpSession.gameId || resolvedGameId;
          resolvedSessionToken = ogpSession.sessionToken || resolvedSessionToken;
          resolvedPlayerId = ogpSession.playerId || resolvedPlayerId;
        }
      }

      if (!resolvedPlayerId && !isEmbeddedInPlayFunPlatform()) {
        resolvedPlayerId = getLocalPlayerId();
      }
      if (!resolvedPlayerId) {
        console.warn('[score-validation] missing playerId, skipping submission', { trigger });
        return;
      }

      const payload: ScoreValidationRequest = {
        gameId: resolvedGameId,
        playerId: resolvedPlayerId,
        sessionToken: resolvedSessionToken,
        sessionStartTimeMs: session.sessionStartTimeMs,
        sessionData: session.sessionData,
      };

      const sessionTokenPreview = resolvedSessionToken
        ? `${resolvedSessionToken.slice(0, 6)}...${resolvedSessionToken.slice(-4)}`
        : undefined;

      for (let attempt = 1; attempt <= SCORE_SUBMIT_MAX_RETRIES; attempt++) {
        try {
          const res = await submitScoreValidation(payload);
          if (!res) {
            if (attempt < SCORE_SUBMIT_MAX_RETRIES) {
              await new Promise((r) => setTimeout(r, SCORE_SUBMIT_RETRY_DELAY_MS * attempt));
              continue;
            }
            console.warn('[score-validation] null response after retries', { trigger });
            return;
          }
          if (!res.ok) {
            const errorMessage = typeof res.error === 'string' ? res.error : '';
            const isForbiddenError =
              errorMessage.includes('"statusCode":403') || errorMessage.toLowerCase().includes('forbidden');
            if (isForbiddenError) {
              console.error('[score-validation] OGP submission forbidden', {
                trigger,
                gameId: resolvedGameId,
                playerId: resolvedPlayerId,
                playerIdHasDidPrefix: typeof resolvedPlayerId === 'string' && resolvedPlayerId.startsWith('did:'),
                hasSessionToken: !!resolvedSessionToken,
                sessionTokenPreview,
              });
            }
            if (NON_RETRYABLE_ERRORS.has(res.error)) {
              console.warn('[score-validation] rejected (non-retryable)', { trigger, error: res.error });
              return;
            }
            if (attempt < SCORE_SUBMIT_MAX_RETRIES) {
              console.warn('[score-validation] retrying', { trigger, attempt, error: res.error });
              await new Promise((r) => setTimeout(r, SCORE_SUBMIT_RETRY_DELAY_MS * attempt));
              continue;
            }
            console.warn('[score-validation] rejected after retries', { trigger, res });
            return;
          }
          if (!res.submissionAttempted) {
            console.warn('[score-validation] validation ok, but no submission attempted', { trigger, res });
          }
          if (res.submissionAttempted) {
            getOpenGameSDK()?.refreshPointsAndMultiplier();
          }
          return;
        } catch (err) {
          if (attempt < SCORE_SUBMIT_MAX_RETRIES) {
            console.warn('[score-validation] error, retrying', { trigger, attempt, err });
            await new Promise((r) => setTimeout(r, SCORE_SUBMIT_RETRY_DELAY_MS * attempt));
            continue;
          }
          console.warn('[score-validation] submission failed after retries', { trigger, err });
          return;
        }
      }
    } finally {
      scoreValidationSettledAtMs = Date.now();
    }
  };

  const getPendingPostSubmissionFreefallMs = () => {
    if (!arcade.isGameOver || !didSubmitScore) {
      return 0;
    }
    if (scoreValidationSettledAtMs === null) {
      return 100;
    }
    return Math.max(0, scoreValidationSettledAtMs + SCORE_SUBMISSION_FREEFALL_MS - Date.now());
  };

  const flushScoreValidation = (trigger: string) => {
    if (REPLAY_MODE || didSubmitScore) {
      return;
    }
    const runData = buildScoreValidationRunData();
    if (!runData) {
      // This can happen if we scheduled due to an early event (e.g. dramaticCollision) but the run hasn't ended yet.
      // Don't permanently disable validation; allow a later game-over to re-schedule and submit.
      didScheduleScoreValidation = false;
      pendingScoreValidationTrigger = null;
      return;
    }
    void submitScoreValidationForRun(trigger, runData);
  };

  const scheduleScoreValidationOnce = (trigger: string) => {
    if (REPLAY_MODE || didSubmitScore || didScheduleScoreValidation) {
      return;
    }
    didScheduleScoreValidation = true;
    if (pendingScoreValidationTimeout !== null) {
      window.clearTimeout(pendingScoreValidationTimeout);
    }
    pendingScoreValidationTrigger = trigger;
    pendingScoreValidationTimeout = window.setTimeout(() => {
      pendingScoreValidationTimeout = null;
      const queuedTrigger = pendingScoreValidationTrigger ?? trigger;
      pendingScoreValidationTrigger = null;
      flushScoreValidation(queuedTrigger);
    }, SCORE_VALIDATION_DELAY_MS);
  };

  const consumeAnchorForSession = async () => {
    const nowMs = Date.now();
    if (isAnchorFresh(nextAnchor, nowMs)) {
      const anchor = nextAnchor as SolPriceAnchor;
      nextAnchor = null;
      return anchor;
    }
    if (nextAnchorPromise && isAnchorRequestFresh(nextAnchorRequestedAtMs, nowMs)) {
      try {
        const anchor = await nextAnchorPromise;
        if (isAnchorFresh(anchor, Date.now())) {
          nextAnchor = null;
          return anchor;
        }
      } catch (err) {
        // Fall through to a fresh fetch.
      }
    }

    // Invalidate any stale in-flight request so its result cannot overwrite.
    nextAnchorRequestId += 1;
    nextAnchorPromise = null;
    nextAnchorRequestedAtMs = null;
    nextAnchor = null;

    const freshStartTimeMs = Date.now();
    return await fetchSolPriceAnchorLast5Minutes(freshStartTimeMs);
  };

  queueNextAnchorPrefetch('init');

  const depth = 250;
  const cameraDistance = 2500;
  const camera = new PerspectiveCamera(Math.PI * 0.15, 1, cameraDistance - depth - 1, cameraDistance + depth + 1);
  camera.position.z = cameraDistance;

  // Main arcade scene
  const arcadeScene = new Scene3D(camera);
  arcadeScene.zIndex = 1;
  engine.addScene(arcadeScene);

  // if (assets.farBg0 && assets.farBg0.set && assets.farBg0.set[0]) {
  //   const farBgTexture = assets.farBg0.set[0];
  //   farBgTexture.setTextureOptions({
  //     width: farBgTexture.width,
  //     height: farBgTexture.height,
  //     minificationFilter: 'NEAREST',
  //     magnificationFilter: 'NEAREST'
  //   });
  // }

  // const farBackground = new QT3D();
  // farBackground.textureSet = assets.farBg0;
  // farBackground.opacity = opacities.OPAQUE;
  // farBackground.castShadow = false;
  // farBackground.receiveShadow = false;
  // farBackground.transform.position.z = -depth - 0.5;
  // farBackground.transform.pivot = 0.5;
  // arcadeScene.addObject(farBackground);

  const starDepth = -depth - 0.6;
  const starField = new CCContainer3D(['position', 'vertices', 'color'], 512);
  starField.opacity = opacities.OPAQUE;
  starField.castShadow = false;
  starField.receiveShadow = false;
  starField.transform.position.z = starDepth;
  arcadeScene.addObject(starField);

  const buildStarField = () => {
    const viewHeight = 2 * camera.getHeight(camera.position.z - starDepth);
    const viewWidth = viewHeight * engine.renderer.width / engine.renderer.height;
    const overscan = 1.25;
    const halfW = viewWidth * 0.5 * overscan;
    const halfH = viewHeight * 0.5 * overscan;

    const area = viewWidth * viewHeight;
    const starCount = Math.max(140, Math.round(area / 18000));
    for (let i = 0; i < starCount; i++) {
      const star = starField.createParticle();
      star.width = 1;
      star.height = 1;
      star.depth = 1;
      // Anchor depth to the far face so stars don't protrude toward the camera.
      star.pivots.x = 0.5 * star.width;
      star.pivots.y = 0.5 * star.height;
      star.pivots.z = star.depth;

      const size = 2 + Math.pow(Math.random(), 1.8) * 6;
      star.scale = size;

      star.position.x = (Math.random() * 2 - 1) * halfW;
      star.position.y = (Math.random() * 2 - 1) * halfH;
      star.position.z = 0;

      const warm = Math.random() < 0.22;
      const brightness = 0.85 + Math.random() * 0.15;
      star.color.r = brightness;
      star.color.g = brightness * (warm ? 0.95 : 1.0);
      star.color.b = brightness * (warm ? 0.75 : 1.0);
      star.color.lobe = 0.01;
    }
  };
  buildStarField();

  // const tapToStart = new BitmapText3D();
  // tapToStart.bitmapText.font = 'mplus';
  // tapToStart.bitmapText.size = 64;
  // let text = 'Tap'
  // // text += 'qwertyuiopasdfghjklzxcvbnmQWERTYUIOPASDFGHJKLZXCVBNM';
  // // text += 'アイウエオカキクケコガギグゲゴサシスセソザジズゼゾタチツ';
  // // text += 'アイウエオカキクケコガギグゲゴサシスセソザジズゼゾタチツテトダヂヅデドナニヌネノハヒフヘホバビブベボパピプペポマミムメモヤユヨラリルレロワヰヱヲ';
  // // text += 'あいうえおかきくけこがぎぐげごさしすせそざじずぜぞたちつてとだぢづでどなにぬねのはひふへほばびぶべぼぱぴぷぺぽまみむめもやゆよらりるれろわゐゑを';
  // // text += '日一国会人年大十二本中長出三同時政事自行社見月分議後前民生連五発間対上部東者党地合市業内相方四定今回新場金員九入選立開手米力学問高代明実円関決子動京全目表戦経通外最言氏現理調体化田当八六約主題下首意法不来作性的要用制治度務強気小七成期公持野協取都和統以機平総加山思家話世受区領多県続進正安設保改数記院女初北午指権心界支第産結百派点教報済書府活原先共得解名交資予川向際査勝面委告軍文反元重近千考判認画海参売利組知案道信策集在件団別物側任引使求所次水半品昨論計死官増係感特情投示変打男基私各始島直両朝革価式確村提運終挙果西勢減台広容必応演電歳住争談能無再位置企真流格有疑口過局少放税検藤町常校料沢裁状工建語球営空職証土与急止送援供可役構木割聞身費付施切由説転食比難防補車優夫研収断井何南石足違消境神番規術護展態導鮮備宅害配副算視条幹独警宮究育席輸訪楽起万着乗店述残想線率病農州武声質念待試族象銀域助労例衛然早張映限親額監環験追審商葉義伝働形景落欧担好退準賞訴辺造英被株頭技低毎医復仕去姿味負閣韓渡失移差衆個門写評課末守若脳極種美岡影命含福蔵量望松非撃佐核観察整段横融型白深字答夜製票況音申様財港識注呼渉達';
  // // text += '良響阪帰針専推谷古候史天階程満敗管値歌買突兵接請器士光討路悪科攻崎督授催細効図週積丸他及湾録処省旧室憲太橋歩離岸客風紙激否周師摘材登系批郎母易健黒火戸速存花春飛殺央券赤号単盟座青破編捜竹除完降超責並療従右修捕隊危採織森競拡故館振給屋介読弁根色友苦就迎走販園具左異歴辞将秋因献厳馬愛幅休維富浜父遺彼般未塁貿講邦舞林装諸夏素亡劇河遣航抗冷模雄適婦鉄寄益込顔緊類児余禁印逆王返標換久短油妻暴輪占宣背昭廃植熱宿薬伊江清習険頼僚覚吉盛船倍均億途圧芸許皇臨踏駅署抜壊債便伸留罪停興爆陸玉源儀波創障継筋狙帯延羽努固闘精則葬乱避普散司康測豊洋静善逮婚厚喜齢囲卒迫略承浮惑崩順紀聴脱旅絶級幸岩練押軽倒了庁博城患締等救執層版老令角絡損房募曲撤裏払削密庭徒措仏績築貨志混載昇池陣我勤為血遅抑幕居染温雑招奈季困星傷永択秀著徴誌庫弾償刊像功拠香欠更秘拒刑坂刻底賛塚致抱繰服犯尾描布恐寺鈴盤息宇項喪伴遠養懸戻街巨震願絵希越契掲躍棄欲痛触邸依籍汚縮還枚属笑互複慮郵束仲栄札枠似夕恵板列露沖探逃借緩節需骨射傾届曜遊迷夢巻購揮君燃充雨閉緒跡包駐貢鹿弱却端賃折紹獲郡併草徹飲貴埼衝焦奪雇災浦暮替析預焼簡譲称肉納樹挑章臓律誘紛貸至宗促慎控';
  // // text += '贈智握照宙酒俊銭薄堂渋群銃悲秒操携奥診詰託晴撮誕侵括掛謝双孝刺到駆寝透津壁稲仮暗裂敏鳥純是飯排裕堅訳盗芝綱吸典賀扱顧弘看訟戒祉誉歓勉奏勧騒翌陽閥甲快縄片郷敬揺免既薦隣悩華泉御範隠冬徳皮哲漁杉里釈己荒貯硬妥威豪熊歯滞微隆埋症暫忠倉昼茶彦肝柱喚沿妙唱祭袋阿索誠忘襲雪筆吹訓懇浴俳童宝柄驚麻封胸娘砂李塩浩誤剤瀬趣陥斎貫仙慰賢序弟旬腕兼聖旨即洗柳舎偽較覇兆床畑慣詳毛緑尊抵脅祝礼窓柔茂犠旗距雅飾網竜詩昔繁殿濃翼牛茨潟敵魅嫌魚斉液貧敷擁衣肩圏零酸兄罰怒滅泳礎腐祖幼脚菱荷潮梅泊尽杯僕桜滑孤黄煕炎賠句寿鋼頑甘臣鎖彩摩浅励掃雲掘縦輝蓄軸巡疲稼瞬捨皆砲軟噴沈誇祥牲秩帝宏唆鳴阻泰賄撲凍堀腹菊絞乳煙縁唯膨矢耐恋塾漏紅慶猛芳懲郊剣腰炭踊幌彰棋丁冊恒眠揚冒之勇曽械倫陳憶怖犬菜耳潜珍梨仁克岳概拘墓黙須偏雰卵遇湖諮狭喫卓干頂虫刷亀糧梶湯箱簿炉牧殊殖艦溶輩穴奇慢鶴謀暖昌拍朗丈鉱寛覆胞泣涙隔浄匹没暇肺孫貞靖鑑飼陰銘鋭随烈尋渕稿枝丹啓也丘棟壌漫玄粘悟舗妊塗熟軒旭恩毒騰往豆遂晩狂叫栃岐陛緯培衰艇屈径淡抽披廷錦准暑拝磯奨妹浸剰胆氷繊駒乾虚棒寒孜霊帳悔諭祈惨虐翻墜沼据肥徐糖搭姉髪忙盾脈滝拾軌俵妨盧粉擦鯨';
  // tapToStart.bitmapText.string = text;
  
  // // Force layout computation to create the texture before we try to access it
  // tapToStart.bitmapText.computeLayout();
  
  // tapToStart.transform.position.x = -300;
  // tapToStart.transform.position.y = 0;
  // tapToStart.transform.position.z = -depth / 2;
  // tapToStart.transform.scale = 1.0;
  // arcadeScene.addObject(tapToStart);

  // console.error('font texture', text.length, tapToStart.textureSet.set[0])

  arcadeScene.ambientLight.intensity = 0.75;

  const directionalLight = arcadeScene.directionalLight;
  directionalLight.intensity = 1.0 - arcadeScene.ambientLight.intensity;
  directionalLight.castShadow = false;
  // directionalLight.castShadow = true;
  directionalLight.direction.x = -0.12;
  directionalLight.direction.y = -0.12;
  directionalLight.direction.z = -1.0;

  // Gameplay scene, contains most of the gameplay elements, separated for rendering purpose
  const gameplayScene = new Scene3D(camera);
  gameplayScene.renderToTexture = true;
  gameplayScene.zIndex = 0;
  engine.addScene(gameplayScene);

  // uncomment to add font texture to scene
  // const fontAtlas = new QT3D();
  // fontAtlas.textureSet = tapToStart.textureSet;
  // fontAtlas.castShadow = false;
  // fontAtlas.transform.position.x = -400;
  // fontAtlas.transform.position.y = -200;
  // fontAtlas.transform.position.z = -depth + 5;
  // fontAtlas.transform.scales.x = tapToStart.textureSet.set[0].width;
  // fontAtlas.transform.scales.y = tapToStart.textureSet.set[0].height;
  // fontAtlas.transform.pivot = 0.5;
  // gameplayScene.addObject(fontAtlas);

  const mplusFont = getSDFFont('mplus', true);
  if (mplusFont) {
    // Preload glyphs that we swap in dynamically (e.g. the altitude overlay).
    mplusFont.updateWithCharacters('0123456789m- ', 32);
    // Note: onResize callback may need to be implemented if needed
    // The SDFFont doesn't have onResize method like the old FontTexture did
  }

  gameplayScene.ambientLight.intensity = arcadeScene.ambientLight.intensity;
  gameplayScene.directionalLight.intensity = arcadeScene.directionalLight.intensity;
  gameplayScene.directionalLight.direction = arcadeScene.directionalLight.direction.copy();

  const logoDepthStart = -depth + 200;
  const logoDepthEnd = -depth + 70;
  const logoCount = 6;
  const logoBaseY = 400;
  const logos: QT3D[] = [];

  for (let l = 0; l < logoCount; l ++) {
    const gameLogo = new QT3D();
    gameLogo.textureSet = assets.gameLogoBlack;
    gameLogo.castShadow = false;
    gameLogo.transform.position.x = 0;
    gameLogo.transform.position.y = logoBaseY;
    gameLogo.transform.position.z = logoDepthEnd + (logoDepthStart - logoDepthEnd) * l / (logoCount - 1);
    gameLogo.transform.scales.x = 600;
    gameLogo.transform.scales.y = 292;
    gameLogo.transform.pivot = 0.5;
    gameplayScene.addObject(gameLogo);
    logos.push(gameLogo);
  }

  // IMPORTANT: `Scroller` currently creates `dollyText` as a TRANSLUSCENT object in `arcadeScene`.
  // `CopyOutlined` / `CopyDark` are also TRANSLUSCENT and don't write depth, so draw order matters:
  // put scroller (and dollyText) *before* the post-process quads so the gameplay composite appears on top.
  const scroller = new Scroller(assets, arcadeScene, depth);

  const shadow = new CopyDark();
  shadow.textureSet = gameplayScene.renderTextureSet;
  shadow.castShadow = true;
  shadow.transform.position.x = -20;
  shadow.transform.position.y = -15;
  shadow.transform.position.z = -depth - 0.25;
  shadow.transform.scales.x = 720;
  shadow.transform.scales.y = -1280;
  shadow.transform.pivot = 0.5;
  shadow.depthFunc = 'GREATER';
  arcadeScene.addObject(shadow);

  const outlinedScene = new CopyOutlined();
  outlinedScene.textureSet = gameplayScene.renderTextureSet;
  outlinedScene.castShadow = true;
  outlinedScene.transform.position.x = 0;
  outlinedScene.transform.position.y = 0;
  outlinedScene.transform.position.z = -depth / 3;
  outlinedScene.transform.scales.x = 720;
  outlinedScene.transform.scales.y = -1280;
  outlinedScene.transform.pivot = 0.5;
  outlinedScene.greyScale = 0;
  arcadeScene.addObject(outlinedScene);

  // Shared fixed-step dt (integer) used for deterministic replay on both client and server.
  const replayFixedTimestep = Number.isFinite((REPLAY_SESSION as any).fixedTimestep)
    ? (REPLAY_SESSION as any).fixedTimestep
    : Number.isFinite((REPLAY_SESSION as any).dtMs)
      ? (REPLAY_SESSION as any).dtMs
      : ARCADE_SIM_DT_MS;
  const physicsStepMs = REPLAY_MODE ? replayFixedTimestep : ARCADE_SIM_DT_MS;
  const postGameOverSteps = Math.ceil(SCORE_VALIDATION_DELAY_MS / physicsStepMs);
  let physicsAccumulator = 0;
  let renderThrottleAccumulator = 0;
  let frameStartPerfMs: number | null = null;
  let frameStartSimMs: number = 0;
  let frameDtMs: number = 0;

  const queueInputEvent = (
    thrustActive: boolean,
    thrustVector: { x: number; y: number },
    eventTimeStamp?: number
  ) => {
    if (REPLAY_MODE || !hasSessionStarted || needsInputToResume) {
      return;
    }
    let step = Math.max(0, simStepIndex);
    if (thrustActive) {
      const normalizedTs = normalizeEventTimeStamp(eventTimeStamp);
      if (normalizedTs !== null && frameStartPerfMs !== null && frameDtMs > 0) {
        const rawOffset = normalizedTs - frameStartPerfMs;
        if (rawOffset >= 0 && rawOffset <= frameDtMs * 2) {
          const offsetMs = Math.max(0, Math.min(frameDtMs, rawOffset));
          const simTimeMs = frameStartSimMs + offsetMs;
          step = Math.max(0, Math.floor(simTimeMs / physicsStepMs));
        }
      }
    }
    if (step > simStepIndex + 1) {
      step = simStepIndex + 1;
    }
    const x = thrustActive ? roundToSigDigits(thrustVector.x, 3) : 0;
    const y = thrustActive ? roundToSigDigits(thrustVector.y, 3) : 1;

    const last = pendingInputEvents[pendingInputEvents.length - 1];
    if (last) {
      if (last.step === step) {
        last.thrustActive = thrustActive;
        last.thrustVector.x = x;
        last.thrustVector.y = y;
        return;
      }
      if (last.thrustActive === thrustActive && last.thrustVector.x === x && last.thrustVector.y === y) {
        return;
      }
    }

    pendingInputEvents.push({ step, thrustActive, thrustVector: { x, y } });
    if (step <= simStepIndex) {
      simInputState = { thrustActive, thrustVector: { x, y } };
    }
  };

  const applyPendingInputEvents = () => {
    while (nextInputEventIndex < pendingInputEvents.length) {
      const evt = pendingInputEvents[nextInputEventIndex];
      if (evt.step > simStepIndex) {
        break;
      }
      simInputState = {
        thrustActive: evt.thrustActive,
        thrustVector: { x: evt.thrustVector.x, y: evt.thrustVector.y },
      };
      nextInputEventIndex += 1;
    }
    if (nextInputEventIndex > 128 && nextInputEventIndex > pendingInputEvents.length / 2) {
      pendingInputEvents = pendingInputEvents.slice(nextInputEventIndex);
      nextInputEventIndex = 0;
    }
  };

  // Altimeter overlay element (shows highest altitude reached)
  const leftAltimeter = new BitmapText3D();
  leftAltimeter.bitmapText.font = 'mplus';
  leftAltimeter.bitmapText.size = 32;
  leftAltimeter.bitmapText.string = '0m';
  leftAltimeter.transform.position.z = -depth / 2;
  arcadeScene.addObject(leftAltimeter);

  // Track the highest altitude reached (sim-derived, FPS-independent).
  let simMaxAltitude = 0;
  let simMaxAltitudeY = -280;
  // Track the highest altitude reached for visuals (interpolated, UI-only).
  let displayMaxAltitude = 0;
  let displayMaxAltitudeY = -280;

  // const rightAltimeter = new BitmapText3D();
  // rightAltimeter.bitmapText.font = 'mplus';
  // rightAltimeter.bitmapText.size = 32;
  // rightAltimeter.transform.position.z = -depth / 2;
  // arcadeScene.addObject(rightAltimeter);

  const altimeterFade = { alpha: 0 };
  let hasRevealedAltimeter = false;
  const syncAltimeterAlpha = () => {
    const alphaValue = altimeterFade.alpha;
    leftAltimeter.setAllCharactersAlpha(alphaValue);
    // rightAltimeter.setAllCharactersAlpha(alphaValue);
  };
  const revealAltimeter = () => {
    if (hasRevealedAltimeter) {
      return;
    }
    altimeterFade.alpha = 0;
    syncAltimeterAlpha();
    arcadeScene.moves
      .move(altimeterFade)
      .lerp({ alpha: 1 }, 500);
    hasRevealedAltimeter = true;
  };
  syncAltimeterAlpha();

  const gameOverTitles = [
    'RECKT',
    'BAA-D LANDING',
    'CUBED',
    'BLOCKED',
    'WOOLED',
    'SHEARED',
    'WOOL-OPS',
    'FLOCKED',
    'LAMB-LED',
    'RUGGED',
    'DUMPED',
    'ZEROED',
    'TOTALLED',
    'WIPED OUT',
    'OBLITERATED',
    'MOON? NO.',
    'CRUSHED',
    'SMASHED',
    'SHATTERED',
    'FLATTENED',
  ];
  const gameOverTitle = new BitmapText3D();
  gameOverTitle.bitmapText.font = 'mplus';
  gameOverTitle.bitmapText.size = 83;
  gameOverTitle.bitmapText.multiline = false;
  gameOverTitle.transform.position.z = -depth / 2;
  gameOverTitle.castShadow = false;
  arcadeScene.addObject(gameOverTitle);

  const gameOverTitleState = { alpha: 0, scale: 0.9 };
  let gameOverTitleMove: any | null = null;
  const syncGameOverTitle = () => {
    gameOverTitle.setAllCharactersAlpha(gameOverTitleState.alpha);
    gameOverTitle.transform.scale = gameOverTitleState.scale;
  };
  const setGameOverTitleText = (text: string) => {
    gameOverTitle.bitmapText.string = text;
    gameOverTitle.bitmapText.computeLayout();

    const chars = gameOverTitle.bitmapText.characters;
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    for (let i = 0; i < chars.length; i++) {
      const ch = chars[i];
      if (ch.width === 0 && ch.height === 0) {
        continue;
      }
      minX = Math.min(minX, ch.x);
      minY = Math.min(minY, ch.y);
      maxX = Math.max(maxX, ch.x + ch.width);
      maxY = Math.max(maxY, ch.y + ch.height);
    }
    if (Number.isFinite(minX) && Number.isFinite(minY)) {
      gameOverTitle.transform.pivots.x = minX + (maxX - minX) * 0.5;
      gameOverTitle.transform.pivots.y = minY + (maxY - minY) * 0.5;
    }

    syncGameOverTitle();
  };

  const arcade = {
    isGameOver: false,
    onGameOver: function () {
      if (this.isGameOver) {
        return;
      }

      this.isGameOver = true;
      scoreValidationSettledAtMs = null;
      scheduleScoreValidationOnce('gameOver');
      // Keep stepping physics during the game-over sequence (so the sheep continues moving),
      // but after we reset the run we will pause until the next user input.
      pauseAfterReset = true;
      // Ensure deterministic input state returns to "no thrust" immediately.
      forceStopThrust();

      const gameOverTitleText = gameOverTitles[Math.floor(Math.random() * gameOverTitles.length)];
      gameOverTitleState.alpha = 0;
      gameOverTitleState.scale = 0.9;
      setGameOverTitleText(gameOverTitleText);
      if (gameOverTitleMove) {
        gameOverTitleMove.clear();
      }
      gameOverTitleMove = arcadeScene.moves
        .move(gameOverTitleState)
        .tempo(300)
        .lerp({ alpha: 1, scale: 1 }, 350)
        .ease(arcadeScene.moves.ease.backOut, 1.5);

      arcadeScene.moves
        .move(outlinedScene)
        .tempo(300)
        .lerp({ greyScale: 1 }, 500)
        .tempo(4000)
        .then(() => {
          this.onStart();
        });
    },
    onStart: function () {
      if (!didSubmitScore && didScheduleScoreValidation) {
        if (pendingScoreValidationTimeout !== null) {
          window.clearTimeout(pendingScoreValidationTimeout);
          pendingScoreValidationTimeout = null;
        }
        const trigger = pendingScoreValidationTrigger ?? 'gameOver';
        pendingScoreValidationTrigger = null;
        flushScoreValidation(trigger);
      }

      const pendingPostSubmissionFreefallMs = getPendingPostSubmissionFreefallMs();
      if (pendingPostSubmissionFreefallMs > 0) {
        window.setTimeout(() => {
          if (this.isGameOver) {
            this.onStart();
          }
        }, Math.min(100, pendingPostSubmissionFreefallMs));
        return;
      }

      this.isGameOver = false;
      didLogGameOverSession = false;
      didSubmitScore = false;
      scoreValidationSettledAtMs = null;
      didScheduleScoreValidation = false;
      gameOverTick = null;
      gameOverStepIndex = null;
      simStepIndex = 0;
      simInputState = { thrustActive: false, thrustVector: { x: 0, y: 1 } };
      pendingInputEvents = [];
      nextInputEventIndex = 0;
      frameStartPerfMs = null;
      frameStartSimMs = 0;
      frameDtMs = 0;
      gameSimSession = null;
      didEndGameSim = false;
      lastStepResult = null;

      // Reset max altitude trackers and Y positions
      simMaxAltitude = 0;
      simMaxAltitudeY = -280;
      displayMaxAltitude = 0;
      displayMaxAltitudeY = -280;
      leftAltimeter.bitmapText.string = '- 0m';
      // rightAltimeter.bitmapText.string = '-';
      altimeterFade.alpha = 0;
      syncAltimeterAlpha();
      hasRevealedAltimeter = false;
      if (REPLAY_MODE) {
        revealAltimeter();
      }

      if (gameOverTitleMove) {
        gameOverTitleMove.clear();
      }
      gameOverTitleState.alpha = 0;
      gameOverTitleState.scale = 0.9;
      syncGameOverTitle();

      arcadeScene.moves
        .move(outlinedScene)
        .lerp({ greyScale: 0 }, 500);

      // Reset per-run state at the beginning of EVERY game (authoritative state is in sim).
      // Recreate the sim to ensure the physics engine starts from a pristine state.
      if (nonce) {
        if (REPLAY_MODE && replayCursor) {
          replayCursor.reset();
        }
        if (!gameSim) {
          gameSim = createGameSimulation({
            fixedTimestep: physicsStepMs,
            resetFn: ({ nonce, simRng: _simRng }) => {
              sim = new ArcadeSim(nonce);
            },
            updateFn: ({ inputs, simRng: _simRng }) => {
              if (!sim) {
                throw new Error('sim_not_initialized');
              }
              const thrust = inputs.find((i) => i.id === 'thrust') || null;
              const stepInput: ArcadeSimInput = thrust
                ? {
                    thrustActive: true,
                    thrustVector: { x: thrust.data?.[0] ?? 0, y: thrust.data?.[1] ?? 1 },
                  }
                : { thrustActive: false, thrustVector: { x: 0, y: 1 } };
              lastStepResult = sim.step(physicsStepMs, stepInput);
            },
            endFn: () => {
              // Must be fully derivable from deterministic sim state; this game tracks max altitude from snapshots.
              return Math.max(0, simMaxAltitude);
            },
          });
        }
        gameSim.reset(nonce);
      } else {
        sim = null;
      }
      world.reset();
      greatReset();

      // Prefetch the next session anchor while this run is starting.
      queueNextAnchorPrefetch('onStart');

      // reset interpolation baseline
      physicsAccumulator = 0;
      if (sim) {
        const snap = sim.getSnapshot();
        prevSnapshot = snap;
        currSnapshot = snap;
      }

      // If this restart came from a collision game-over, pause until the next user input.
      if (pauseAfterReset) {
        needsInputToResume = true;
        pauseAfterReset = false;
      }
    },
  };

  const world = new World(assets, gameplayScene, depth);
  const sheep = new Sheep(assets, gameplayScene, depth);
  const canvas = engine.renderer.canvas as HTMLCanvasElement;
  canvas.tabIndex = 0;

  const KEYBOARD_THRUST_IDS: Record<string, string> = {
    ArrowLeft: 'key:ArrowLeft',
    ArrowRight: 'key:ArrowRight',
    ArrowDown: 'key:ArrowDown',
    a: 'key:a',
    s: 'key:s',
    d: 'key:d'
  };

  const KEYBOARD_THRUST_KEYS = new Set(Object.keys(KEYBOARD_THRUST_IDS));
  const keyboardActiveKeys = new Set<string>();

  // Keyboard emulates stable screen-space touch points, not sheep-relative points.
  // This keeps "down" straight up even after lateral movement.
  const KEY_SHEEP_SCALE = 0.88 * 0.82;
  const KEYBOARD_TOUCH_OFFSET_X = 200 * KEY_SHEEP_SCALE;
  const KEYBOARD_TOUCH_OFFSET_Y = 70 * KEY_SHEEP_SCALE;

  const buildKeyboardTouch = (key: string) => {
    const renderer = engine.renderer;
    const width = Math.max(1, renderer.styleWidth);
    const height = Math.max(1, renderer.styleHeight);
    const baseX = width * 0.5;
    const baseY = height * 0.65 + KEYBOARD_TOUCH_OFFSET_Y;

    let offsetX = 0;
    if (key === 'ArrowLeft' || key === 'a') {
      offsetX = -KEYBOARD_TOUCH_OFFSET_X;
    } else if (key === 'ArrowRight' || key === 'd') {
      offsetX = KEYBOARD_TOUCH_OFFSET_X;
    }

    return {
      id: KEYBOARD_THRUST_IDS[key],
      position: { x: baseX + offsetX, y: baseY }
    };
  };

  const getReplayNonceParts = (): string[] => {
    if (Array.isArray((REPLAY_SESSION as any).nonce)) {
      return ((REPLAY_SESSION as any).nonce as string[]).map(String);
    }
    if (Array.isArray((REPLAY_SESSION as any).seedParts)) {
      return ((REPLAY_SESSION as any).seedParts as string[]).map(String);
    }
    return [];
  };

  const getReplayPackedInputsById = () => {
    const provided = (REPLAY_SESSION as any).packedInputsById;
    if (provided && typeof provided === 'object') {
      return provided as any;
    }
    const packed = Array.isArray((REPLAY_SESSION as any).inputsPacked) ? ((REPLAY_SESSION as any).inputsPacked as number[]).slice() : [];
    return { thrust: { stride: 4, packed } };
  };

  const replayPackedInputsById = REPLAY_MODE ? getReplayPackedInputsById() : null;
  const replayCursor = REPLAY_MODE ? createPackedInputsCursor(replayPackedInputsById as any) : null;

  const arcadeUpdater = new GameObject({
    t: 0
  }, function globalUpdate (this: GameObject, dt: number) {
    if (DEBUG_RENDER_FPS > 0) {
      renderThrottleAccumulator += dt;
      const minFrameMs = 1000 / DEBUG_RENDER_FPS;
      if (renderThrottleAccumulator < minFrameMs) {
        return;
      }
      dt = renderThrottleAccumulator;
      renderThrottleAccumulator = 0;
    }
    frameDtMs = dt;
    frameStartPerfMs = getPerfNowMs();
    if (gameSim && !didEndGameSim) {
      simStepIndex = gameSim.getState().tick;
    }
    frameStartSimMs = simStepIndex * physicsStepMs + physicsAccumulator;

    // Step physics ONLY after the first player interaction (session start).
    // This prevents any gameplay progression / randomness consumption pre-interaction.
    const shouldRunGameSim = hasSessionStarted && sim && gameSim && !needsInputToResume && !didEndGameSim;
    const shouldRunPostSubmitFreefall =
      hasSessionStarted &&
      sim &&
      !needsInputToResume &&
      didEndGameSim &&
      arcade.isGameOver &&
      getPendingPostSubmissionFreefallMs() > 0;

    if (shouldRunGameSim) {
      const activeGameSim = gameSim as NonNullable<typeof gameSim>;
      physicsAccumulator += dt;
      while (physicsAccumulator >= physicsStepMs) {
        const stepStart = activeGameSim.getState().tick;
        let stepInput: ArcadeSimInput;
        let tickInputs: GameSimInput[] = [];
        if (REPLAY_MODE) {
          tickInputs = replayCursor ? replayCursor.getTickInputs(stepStart) : [];
          const thrust = tickInputs.find((i) => i.id === 'thrust') || null;
          stepInput = thrust
            ? {
                thrustActive: true,
                thrustVector: { x: thrust.data?.[0] ?? 0, y: thrust.data?.[1] ?? 1 },
              }
            : { thrustActive: false, thrustVector: { x: 0, y: 1 } };

          sheep.thrustActive = stepInput.thrustActive;
          sheep.thrustVector.x = stepInput.thrustVector.x;
          sheep.thrustVector.y = stepInput.thrustVector.y;
        } else {
          applyPendingInputEvents();
          stepInput = {
            thrustActive: simInputState.thrustActive,
            thrustVector: {
              x: simInputState.thrustVector.x,
              y: simInputState.thrustVector.y,
            }
          };
          if (!stepInput.thrustActive) {
            // Keep a stable default direction for the sim when not thrusting.
            stepInput.thrustVector.x = 0;
            stepInput.thrustVector.y = 1;
          }
          if (stepInput.thrustActive) {
            tickInputs.push({ id: 'thrust', data: [stepInput.thrustVector.x, stepInput.thrustVector.y] });
          }
        }
        activeGameSim.update(tickInputs);
        simStepIndex = activeGameSim.getState().tick;
        const res = lastStepResult;
        if (!res) {
          throw new Error('missing_step_result');
        }

        // Game-over is authoritative from simulation events.
        for (let i = 0; i < res.events.length; i++) {
          const e = res.events[i];
          if (e.type === 'dramaticCollision') {
            world.playCollisionFxForCubeId(e.withCubeId, { dramatic: true });
            scheduleScoreValidationOnce('dramaticCollision');
          } else if (e.type === 'gameOver') {
            if (gameOverTick === null) {
              gameOverTick = e.tick;
              gameOverStepIndex = Math.max(0, e.tick - 1);
            }
            if (!didLogGameOverSession) {
              didLogGameOverSession = true;
              console.log('[simSession] gameOver', {
                nonce,
                sessionStartTimeMs,
                dtMs: physicsStepMs,
                tick: e.tick,
                simStepIndex,
                nonceAnchorMeta,
              });
            }
            arcade.onGameOver();
          }
        }

        prevSnapshot = currSnapshot ?? res.snapshot;
        currSnapshot = res.snapshot;

        if (gameOverTick === null || simStepIndex - gameOverTick <= postGameOverSteps) {
          const simAltitude = computeAltitude(currSnapshot.sheep.y);
          if (simAltitude > simMaxAltitude) {
            simMaxAltitude = simAltitude;
            simMaxAltitudeY = currSnapshot.sheep.y - 40;
          }
        }

        if (REPLAY_MODE && gameOverTick !== null && simStepIndex - gameOverTick > postGameOverSteps) {
          try {
            gameSimSession = activeGameSim.end();
          } catch (err) {
            console.warn('[replay] failed to end GameSimulation', err);
          }
          didEndGameSim = true;
        }

        physicsAccumulator -= physicsStepMs;
      }
    } else if (shouldRunPostSubmitFreefall) {
      const activeSim = sim as NonNullable<typeof sim>;
      physicsAccumulator += dt;
      while (physicsAccumulator >= physicsStepMs) {
        const res = activeSim.step(physicsStepMs, { thrustActive: false, thrustVector: { x: 0, y: 1 } });
        simStepIndex += 1;

        for (let i = 0; i < res.events.length; i++) {
          const e = res.events[i];
          if (e.type === 'dramaticCollision') {
            world.playCollisionFxForCubeId(e.withCubeId, { dramatic: true });
          }
        }

        prevSnapshot = currSnapshot ?? res.snapshot;
        currSnapshot = res.snapshot;
        physicsAccumulator -= physicsStepMs;
      }
    } else {
      physicsAccumulator = 0;
      prevSnapshot = currSnapshot;
    }

    const alpha = hasSessionStarted ? Math.max(0, Math.min(1, physicsAccumulator / physicsStepMs)) : 0;
    const pSheep = prevSnapshot?.sheep ?? { x: 0, y: -410, angle: 0 };
    const cSheep = currSnapshot?.sheep ?? pSheep;
    const interpolatedSheep = {
      x: lerp(pSheep.x, cSheep.x, alpha),
      y: lerp(pSheep.y, cSheep.y, alpha),
      angle: lerpAngle(pSheep.angle, cSheep.angle, alpha)
    };

    // Visual update only (no physics side effects)
    sheep.updateVisual(dt, interpolatedSheep);
    
    // Update companion visuals
    // world.updateCompanionVisuals(dt);

    const aspect = engine.renderer.height > 0 ? (engine.renderer.width / engine.renderer.height) : 1;
    const { gameHeight: targetGameHeight, heightReduction: cameraHeightReduction } = computeTargetGameHeightForAspect(aspect);

    // Lower the "RocketSheep" title/logo as the camera view height shrinks.
    // (Y axis inverted: increasing Y moves the logo down on screen.)
    for (let i = 0; i < logos.length; i++) {
      logos[i].transform.position.y = logoBaseY - cameraHeightReduction;
    }

    camera.position.x = 0.3 * sheep.visualBody.transform.position.x + 0.7 * camera.position.x;
    // When the view widens and the target camera height shrinks, shift the camera down by the same amount.
    camera.position.y = 0.3 * (sheep.visualBody.transform.position.y + 200 + sheep.dy * 1 - cameraHeightReduction / 3) + 0.7 * camera.position.y;
    if (camera.position.y < -cameraHeightReduction / 3) {
      camera.position.y = -cameraHeightReduction / 3;
    }

    starField.transform.position.x = camera.position.x;
    starField.transform.position.y = camera.position.y;

    outlinedScene.transform.position.x = camera.position.x;
    outlinedScene.transform.position.y = camera.position.y;
    shadow.transform.position.x = camera.position.x - 20;
    shadow.transform.position.y = camera.position.y - 17;

    const scaleFactor = 1.1;
    const viewDistance = camera.position.z - outlinedScene.transform.position.z;
    const targetViewHeight = targetGameHeight / scaleFactor;
    const nextFov = 2 * Math.atan((targetViewHeight * 0.5) / viewDistance);
    if (Number.isFinite(nextFov) && nextFov > 0 && Math.abs(nextFov - camera.fov) > 1e-7) {
      camera.fov = nextFov;
    }

    const viewHeight = 2 * camera.getHeight(viewDistance);
    const screenHeight = targetGameHeight;
    const screenWidthInWorld = viewHeight * aspect;

    // Update altimeter position and display highest altitude reached (visuals)
    const sheepY = sheep.visualBody.transform.position.y;
    if (sheep.altitude > displayMaxAltitude) {
      displayMaxAltitude = sheep.altitude;
      displayMaxAltitudeY = sheepY - 40;
    }

    // Position altimeter at left screen edge, locked at highest Y position achieved
    leftAltimeter.transform.position.x = camera.position.x + 150;
    leftAltimeter.transform.position.y = Math.max(Math.min(displayMaxAltitudeY, camera.position.y + 560), -280);
    leftAltimeter.bitmapText.string = Math.round(simMaxAltitude) + 'm';

    // rightAltimeter.transform.position.x = camera.position.x + (screenWidthInWorld / 2 - 40);
    // rightAltimeter.transform.position.y = Math.max(Math.min(displayMaxAltitudeY, camera.position.y + 560), -280);
    // rightAltimeter.bitmapText.string = '-';
    syncAltimeterAlpha();

    gameOverTitle.transform.position.x = camera.position.x;
    gameOverTitle.transform.position.y = camera.position.y;
    syncGameOverTitle();

    const height = screenHeight;
    const width = height * aspect;
    outlinedScene.transform.scales.x = width;
    outlinedScene.transform.scales.y = -height;
    shadow.transform.scales.x = width;
    shadow.transform.scales.y = -height;

    // Updating environment
    scroller.update();

    // Updating world visuals (interpolated)
    if (prevSnapshot && currSnapshot) {
      world.render(prevSnapshot, currSnapshot, alpha);
    }
  });

  gameplayScene.gameObject.addChild(arcadeUpdater);

  let activeTouches = {};
  let didFocusCanvasForKeyboard = false;

  const focusCanvasForKeyboard = () => {
    if (didFocusCanvasForKeyboard) {
      return;
    }
    didFocusCanvasForKeyboard = true;
    canvas.focus();
  };

  // The "great reset": drop all input state right when the sheep is repositioned.
  // This forces the next thrust to start from a fresh user interaction event.
  const greatReset = () => {
    activeTouches = {};
    keyboardActiveKeys.clear();
    preStartTouches = {};
    pendingStartEvent = null;
    pendingPreStartTouches = null;

    sheep.thrustActive = false;
    // Default thrust direction: straight up.
    sheep.thrustVector.x = 0;
    sheep.thrustVector.y = 1;
    // Reset control-space rotation used while thrusting.
    sheep.controlRotationY = 0;
    sheep.controlRotationZ = 0;
  };

  // In REPLAY_MODE, start the replay immediately from `REPLAY_SESSION` and drive inputs from packed segments.
  if (REPLAY_MODE) {
    nonce = getReplayNonceParts();
    nonceAnchorMeta = (REPLAY_SESSION as any).seedAnchorMeta ?? null;
    sessionStartTimeMs = Number.isFinite(Number(nonce[1])) ? Number(nonce[1]) : null;
    hasSessionStarted = true;
    isInitializingSession = false;
    needsInputToResume = false;

    arcade.onStart();
    console.log('[replay] started', { nonce, fixedTimestep: physicsStepMs });
  }

  const resumeGameplayIfNeeded = () => {
    if (!hasSessionStarted) {
      return;
    }
    if (!needsInputToResume) {
      return;
    }
    if (!sim) {
      return;
    }

    needsInputToResume = false;

    // Reset interpolation baseline to avoid visual snapping after a paused interval.
    physicsAccumulator = 0;
    const snap = sim.getSnapshot();
    prevSnapshot = snap;
    currSnapshot = snap;
    pendingInputEvents = [];
    nextInputEventIndex = 0;
    simInputState = { thrustActive: false, thrustVector: { x: 0, y: 1 } };
    frameStartPerfMs = null;
    frameStartSimMs = 0;
    frameDtMs = 0;
  };

  const startThrust = function (event, touches) {
    if (REPLAY_MODE) {
      return;
    }
    if (event?.type === 'mousedown' || event?.type === 'touchstart') {
      focusCanvasForKeyboard();
    }
    if (event?.type === 'mousedown') {
      mouseDown = true;
    }
    if (!hasSessionStarted) {
      if (!isInitializingSession) {
        showRngLoadingOverlay();
      }

      // Track touches even before session start so we can honor stop/cancel before init completes.
      for (let t = 0; t < touches.length; t++) {
        const touch = touches[t];
        preStartTouches[touch.id] = touch;
      }

      // If initialization is already in-flight, just keep the latest event/touches buffered.
      pendingStartEvent = event;
      pendingPreStartTouches = touches;
      if (isInitializingSession) {
        return;
      }

      isInitializingSession = true;

      (async () => {
        // Let the browser paint the loading overlay before heavier init work starts.
        await waitForNextPaint();

        // Fetch anchor in parallel with OGP session bootstrap.
        const anchorPromise = consumeAnchorForSession().catch((err) => {
          console.warn('[arcadeSim] Binance anchor fetch failed; falling back to local seed', err);
          return null;
        });

        const ogpSessionPromise = (async () => {
          // Fast attempt: don't block startup on session token/login.
          const quick = await getOGPSession({
            requireLogin: false,
            loginTimeoutMs: 1_500,
            loginRetries: 1,
          }).catch(() => null);
          if (quick?.playerId) {
            return quick;
          }

          // Embedded fallback: some containers provide playerId only after SDK login.
          if (isEmbeddedInPlayFunPlatform()) {
            return await getOGPSession({
              requireLogin: true,
              loginTimeoutMs: 6_000,
              loginRetries: 1,
            }).catch(() => quick);
          }

          return quick;
        })();

        // Fetch OGP player ID and anchor in parallel so the first session
        // uses the real player ID in the nonce (needed for score submission).
        const [ogpSession, anchor] = await Promise.all([ogpSessionPromise, anchorPromise]);

        // Rule: never start a gameplay session before we have a player id.
        // In embedded mode this must come from OGP; in local standalone mode we allow a local fallback id.
        // Fallback read is important when getOGPSession fails but postMessage playerId has already arrived.
        const fallbackPlayerId = await waitForOGPPlayerId(1_500).catch(() => undefined);
        const resolvedPlayerId =
          ogpSession?.playerId ||
          fallbackPlayerId ||
          (!isEmbeddedInPlayFunPlatform() ? getLocalPlayerId() : undefined);
        if (!resolvedPlayerId) {
          isInitializingSession = false;
          hideRngLoadingOverlay();
          console.warn('[arcadeSim] blocked session start: missing OGP playerId', {
            embedded: isEmbeddedInPlayFunPlatform(),
            hasSessionToken: !!ogpSession?.sessionToken,
            hasGameId: !!ogpSession?.gameId,
          });
          return;
        }
        playerKey = resolvedPlayerId;
        resolvedOGPSessionForRun = {
          playerId: resolvedPlayerId,
          gameId: ogpSession?.gameId,
          sessionToken: ogpSession?.sessionToken,
        };

        const startTimeMs = anchor?.sessionStartTimeMs ?? Date.now();
        sessionStartTimeMs = startTimeMs;
        if (anchor) {
          nonce = [playerKey, String(startTimeMs), anchor.solPriceSeed];
          nonceAnchorMeta = anchor;
        } else {
          nonce = [playerKey, String(startTimeMs), ''];
          nonceAnchorMeta = null;
        }

        // Enforce the rule above: mark the session as started only after player id resolution succeeds.
        hasSessionStarted = true;
        isInitializingSession = false;
        hideRngLoadingOverlay();

        // Reset state so the session always begins from a known baseline,
        // then apply the buffered first thrust input.
        arcade.onStart();
        if (!arcade.isGameOver) {
          revealAltimeter();
        }

        // Promote pre-start touches into the normal activeTouches map.
        activeTouches = preStartTouches;

        // Only activate thrust if the user hasn't released all touches while we were initializing.
        sheep.thrustActive = Object.keys(activeTouches).length > 0;
        if (sheep.thrustActive) {
          sheep.updateThrust(activeTouches);
        }
        simInputState = sheep.thrustActive
          ? { thrustActive: true, thrustVector: { x: roundToSigDigits(sheep.thrustVector.x, 3), y: roundToSigDigits(sheep.thrustVector.y, 3) } }
          : { thrustActive: false, thrustVector: { x: 0, y: 1 } };

        if (anchor) {
          console.log(
            `[arcadeSim] initialized playerKey="${playerKey}" sessionStart=${startTimeMs} ` +
            `binance2mStart=${anchor.windowStartCandleOpenTimeMs} binance2mEnd=${anchor.windowEndCandleOpenTimeMs} ` +
            `solPriceSeedLen=${anchor.solPriceSeed.length} source=${anchor.source}`
          );
        } else {
          console.log(
            `[arcadeSim] initialized playerKey="${playerKey}" sessionStart=${startTimeMs} ` +
            'binance2mStart=null binance2mEnd=null solPriceSeedLen=0 source=local'
          );
        }

        // If a touch/mouse move happened during init, use the most recent buffered snapshot.
        if (pendingStartEvent && pendingPreStartTouches) {
          moveThrust(pendingStartEvent, pendingPreStartTouches);
        }
      })().catch((err) => {
        isInitializingSession = false;
        hideRngLoadingOverlay();
        console.warn('[arcadeSim] failed to initialize session', err);
      });

      return;
    }

    if (arcade.isGameOver) {
      return;
    }

    revealAltimeter();
    resumeGameplayIfNeeded();
    sheep.thrustActive = true;
    moveThrust(event, touches);
  }

  const moveThrust = function (event, touches) {
    if (REPLAY_MODE) {
      return;
    }
    if (arcade.isGameOver) {
      return;
    }
    if (event?.type === 'mousemove' && !mouseDown) {
      return;
    }

    resumeGameplayIfNeeded();

    // registering new touches
    // console.error('touches', touches.slice())
    for (let t = 0; t < touches.length; t++) {
      const touch = touches[t];
      if (!hasSessionStarted) {
        preStartTouches[touch.id] = touch;
      } else {
        activeTouches[touch.id] = touch;
      }
    }

    if (hasSessionStarted) {
      sheep.updateThrust(activeTouches);
      queueInputEvent(
        sheep.thrustActive === true,
        { x: sheep.thrustVector.x, y: sheep.thrustVector.y },
        event?.timeStamp
      );
    } else {
      pendingStartEvent = event;
      pendingPreStartTouches = touches;
    }
  }

  const stopThrust = function (event, touches) {
    if (REPLAY_MODE) {
      return;
    }
    if (event?.type === 'mouseup') {
      mouseDown = false;
    }
    for (let t = 0; t < touches.length; t++) {
      if (!hasSessionStarted) {
        delete preStartTouches[touches[t].id];
      } else {
        delete activeTouches[touches[t].id];
      }
    }
  
    if (hasSessionStarted) {
      sheep.thrustActive = Object.keys(activeTouches).length > 0;
      if (sheep.thrustActive) {
        sheep.updateThrust(activeTouches);
      }
      queueInputEvent(
        sheep.thrustActive === true,
        { x: sheep.thrustVector.x, y: sheep.thrustVector.y },
        event?.timeStamp
      );
    }
  }

  const forceStopThrust = (event?) => {
    if (REPLAY_MODE) {
      return;
    }
    mouseDown = false;
    activeTouches = {};
    keyboardActiveKeys.clear();
    preStartTouches = {};
    pendingStartEvent = null;
    pendingPreStartTouches = null;
    sheep.thrustActive = false;
    simInputState = { thrustActive: false, thrustVector: { x: 0, y: 1 } };
    if (hasSessionStarted) {
      queueInputEvent(false, { x: sheep.thrustVector.x, y: sheep.thrustVector.y }, event?.timeStamp);
    }
  };

  const getActiveKeyboardTouches = () => {
    const touches: { id: string; position: { x: number; y: number; }; }[] = [];
    for (const key of keyboardActiveKeys) {
      touches.push(buildKeyboardTouch(key));
    }
    return touches;
  };

  const normalizeKeyboardKey = (rawKey: string) => {
    if (rawKey && rawKey.length === 1) {
      return rawKey.toLowerCase();
    }
    return rawKey;
  };

  const startKeyboardThrust = (event) => {
    const key = normalizeKeyboardKey(event.key);
    if (!KEYBOARD_THRUST_KEYS.has(key)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    keyboardActiveKeys.add(key);
    const touches = getActiveKeyboardTouches();
    if (touches.length === 0) {
      return;
    }

    if (event.repeat) {
      if (!sheep.thrustActive) {
        startThrust(event, touches);
      } else {
        moveThrust(event, touches);
      }
      return;
    }

    startThrust(event, touches);
    if (touches.length > 1) {
      moveThrust(event, touches);
    }
  };

  const stopKeyboardThrust = (event) => {
    const key = normalizeKeyboardKey(event.key);
    if (!KEYBOARD_THRUST_KEYS.has(key)) {
      return;
    }

    if (!keyboardActiveKeys.has(key)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    keyboardActiveKeys.delete(key);
    stopThrust(event, [buildKeyboardTouch(key)]);

    const touches = getActiveKeyboardTouches();
    if (touches.length > 0) {
      moveThrust(event, touches);
    }
  };

  engine.addListener('touchstart', startThrust);
  engine.addListener('mousedown', startThrust);

  engine.addListener('touchmove', moveThrust);
  engine.addListener('mousemove', moveThrust);

  engine.addListener('touchend', stopThrust);
  engine.addListener('mouseup', stopThrust);
  engine.addListener('touchcancel', forceStopThrust);
  engine.addListener('mouseout', forceStopThrust);

  engine.addListener('keydown', startKeyboardThrust);
  engine.addListener('keyup', stopKeyboardThrust);

  if (typeof window !== 'undefined') {
    window.addEventListener('blur', forceStopThrust, { passive: true });
    window.addEventListener('mouseup', forceStopThrust, { passive: true });
    window.addEventListener('touchend', forceStopThrust, { passive: true });
    window.addEventListener('touchcancel', forceStopThrust, { passive: true });
  }
}
